import { isLocalhostDevMode } from "@/utils/utils";
import { create } from "zustand";
import { devtools } from "zustand/middleware";
import type { ClientSearchResult } from "./types";

// One shared instance, so resetting an already empty result keeps the reference and re-runs no effect.
const EMPTY_CLIENT_SEARCH_RESULT: ClientSearchResult = {
  keyword: undefined,
  clientType: undefined,
  data: undefined,
};

interface ClientSearchState {
  result: ClientSearchResult;
  runningSearchesCount: number;
  latestSearchId: number;
  actions: {
    searchStarted: () => number;
    searchSucceeded: (searchId: number, result: ClientSearchResult) => void;
    searchFailed: (searchId: number) => void;
    resultDeleted: () => void;
    sessionEnded: () => void;
  };
}

/**
 * The last client search result, shared by every mounted form. It is an event rather than a cache: the forms react to
 * each new result in their effects and match its keyword against their own fields, so it cannot live in the query
 * cache, which would hand back a cached response instead of a new one.
 *
 * Only the latest search sets the result - a response to a superseded one is dropped. The running count still covers
 * the superseded ones, and changes in the same update as the result, so an effect reacting to a new result already
 * sees the search finished.
 *
 * A temporary exception to keeping server state out of Zustand, until client search is reworked onto React Query alone.
 */
const useClientSearchStore = create<ClientSearchState>()(
  devtools(
    (set, get) => ({
      result: EMPTY_CLIENT_SEARCH_RESULT,
      runningSearchesCount: 0,
      latestSearchId: 0,
      actions: {
        searchStarted: () => {
          set((state) => ({
            runningSearchesCount: state.runningSearchesCount + 1,
            latestSearchId: state.latestSearchId + 1,
          }));

          return get().latestSearchId;
        },
        searchSucceeded: (searchId, result) =>
          set((state) => ({
            runningSearchesCount: state.runningSearchesCount - 1,
            result: searchId === state.latestSearchId ? result : state.result,
          })),
        searchFailed: (searchId) =>
          set((state) => ({
            runningSearchesCount: state.runningSearchesCount - 1,
            result: searchId === state.latestSearchId ? EMPTY_CLIENT_SEARCH_RESULT : state.result,
          })),
        resultDeleted: () => set({ result: EMPTY_CLIENT_SEARCH_RESULT }),
        // Also supersedes a search still running, so its response cannot bring the previous account's client back.
        sessionEnded: () =>
          set((state) => ({ result: EMPTY_CLIENT_SEARCH_RESULT, latestSearchId: state.latestSearchId + 1 })),
      },
    }),
    { name: "clientSearch", enabled: isLocalhostDevMode() },
  ),
);

export const useClientSearchResult = () => useClientSearchStore((state) => state.result);

export const useIsClientSearchInProgress = () => useClientSearchStore((state) => state.runningSearchesCount > 0);

export const useClientSearchActions = () => useClientSearchStore((state) => state.actions);

export const clientSearchActions = () => useClientSearchStore.getState().actions;
